/**
 * 8TrackFit - homepage behaviour
 *
 * Integrates with Tinker (Horizon) rather than replacing it:
 *  - add-to-cart POSTs to routes.cart_add_url, then dispatches the theme's
 *    'cart:update' event so <cart-icon> and <cart-items-component> refresh,
 *    and opens <cart-drawer-component> explicitly.
 *  - IMPORTANT: source 'product-form-component' makes cart-icon.js treat
 *    itemCount as a DELTA (added to the bubble), which is what we want.
 */

(() => {
  const CART_UPDATE = 'cart:update';

  // JSON templates reserve {{ }} for dynamic sources, so [amount] is the token
  // merchants type. Both forms are accepted for safety.
  const AMOUNT_TOKEN = /\[amount\]|\{\{\s*amount\s*\}\}/;

  const addUrl = () =>
    (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';

  /** Section ids the cart drawer needs re-rendered after an add. */
  function cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.push(el.dataset.sectionId);
    });
    return ids;
  }

  function announceCartUpdate(quantity, productId, sections) {
    document.dispatchEvent(
      new CustomEvent(CART_UPDATE, {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: 'tf-home',
          // theme code reads .data without optional chaining - always an object
          data: {
            source: 'product-form-component',
            itemCount: quantity,
            productId: productId,
            sections: sections,
          },
        },
      })
    );
  }

  /* ---------------------------------------------------------------- add to cart */

  async function addToCart(form) {
    const button = form.querySelector('[data-tf-atc-button]');
    const idInput = form.querySelector('[name="id"]');
    const variantId = idInput ? idInput.value : null;
    if (!variantId) return;

    const qtyInput = form.querySelector('[name="quantity"]');
    const quantity = Number((qtyInput && qtyInput.value) || 1);
    const original = button ? button.textContent : '';

    if (button) {
      button.disabled = true;
      button.textContent = button.dataset.addingText || 'Adding...';
    }

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', String(quantity));

    const ids = cartSectionIds();
    if (ids.length) body.append('sections', ids.join(','));

    try {
      const response = await fetch(addUrl(), {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
        body,
      });

      const json = await response.json();

      if (!response.ok) {
        throw new Error(json.description || json.message || 'Could not add to cart');
      }

      announceCartUpdate(quantity, form.dataset.productId, json.sections);

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') drawer.open();

      if (button) {
        button.textContent = button.dataset.addedText || 'Added';
        setTimeout(() => {
          button.textContent = original;
          button.disabled = false;
        }, 1600);
      }
    } catch (error) {
      console.error('[tf-home] add to cart failed', error);
      if (button) {
        button.textContent = button.dataset.errorText || 'Unavailable';
        setTimeout(() => {
          button.textContent = original;
          button.disabled = false;
        }, 2200);
      }
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-tf-atc]')) return;
    event.preventDefault();
    addToCart(form);
  });

  /* ------------------------------------------------------- free delivery progress */

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function paintProgress(root, totalCents) {
    const threshold = Number(root.dataset.threshold || 0);
    if (!threshold) return;

    const currency = root.dataset.currency || 'USD';
    const remaining = Math.max(threshold - totalCents, 0);
    const pct = Math.min((totalCents / threshold) * 100, 100);

    const fill = root.querySelector('[data-tf-progress-fill]');
    const label = root.querySelector('[data-tf-progress-label]');
    const amounts = root.querySelector('[data-tf-progress-amounts]');

    if (fill) fill.style.width = pct + '%';
    if (amounts) {
      amounts.textContent =
        formatMoney(totalCents, currency) + ' / ' + formatMoney(threshold, currency);
    }

    if (label) {
      if (remaining === 0) {
        label.innerHTML =
          '<strong>' + (root.dataset.unlockedText || 'Free delivery unlocked') + '</strong>';
      } else {
        const template = root.dataset.awayText || "You're [amount] away from";
        const away = template.replace(AMOUNT_TOKEN, formatMoney(remaining, currency));
        label.innerHTML =
          away + ' <strong>' + (root.dataset.rewardText || 'Free delivery') + '</strong>';
      }
    }
  }

  async function refreshProgress() {
    const bars = document.querySelectorAll('[data-tf-progress]');
    if (!bars.length) return;
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      bars.forEach((bar) => paintProgress(bar, cart.total_price || 0));
    } catch (error) {
      console.error('[tf-home] cart fetch failed', error);
    }
  }

  document.addEventListener(CART_UPDATE, () => {
    // let the theme settle its own cart state first
    setTimeout(refreshProgress, 60);
  });

  /* -------------------------------------------------------------------- wishlist */

  const WISHLIST_KEY = 'tf-wishlist';

  function readWishlist() {
    try {
      return JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode - silently ignore */
    }
  }

  function syncWishlist() {
    const list = readWishlist();
    document.querySelectorAll('[data-tf-wish]').forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        list.indexOf(button.dataset.tfWish) !== -1 ? 'true' : 'false'
      );
    });
  }

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-tf-wish]') : null;
    if (!button) return;
    event.preventDefault();

    const id = button.dataset.tfWish;
    const list = readWishlist();
    const index = list.indexOf(id);

    if (index === -1) list.push(id);
    else list.splice(index, 1);

    writeWishlist(list);
    button.setAttribute('aria-pressed', index === -1 ? 'true' : 'false');
  });

  /* ----------------------------------------------------------------- hero slider */

  const PAUSE_ICON =
    '<svg viewBox="0 0 12 12" aria-hidden="true"><rect x="2" y="1.5" width="3" height="9" rx="0.6"/><rect x="7" y="1.5" width="3" height="9" rx="0.6"/></svg>';
  const PLAY_ICON =
    '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M3 1.6v8.8L10 6z"/></svg>';

  function initHero(root) {
    if (root.dataset.tfHeroReady === 'true') return;
    root.dataset.tfHeroReady = 'true';

    const slides = Array.prototype.slice.call(root.querySelectorAll('[data-tf-slide]'));
    if (slides.length < 2) return;

    const dots = Array.prototype.slice.call(root.querySelectorAll('[data-tf-dot]'));
    const pause = root.querySelector('[data-tf-pause]');
    const interval = Number(root.dataset.autoplay || 0) * 1000;

    let index = 0;
    let timer = null;
    let playing = interval > 0;

    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => slide.toggleAttribute('hidden', i !== index));
      dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
    };

    const stop = () => {
      if (timer) clearInterval(timer);
      timer = null;
    };
    const start = () => {
      if (!interval) return;
      stop();
      timer = setInterval(() => show(index + 1), interval);
    };

    dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        show(i);
        if (playing) start();
      })
    );

    if (pause) {
      pause.addEventListener('click', () => {
        playing = !playing;
        pause.setAttribute('aria-pressed', String(!playing));
        pause.setAttribute('aria-label', playing ? 'Pause slideshow' : 'Play slideshow');
        pause.innerHTML = playing ? PAUSE_ICON : PLAY_ICON;
        if (playing) start();
        else stop();
      });
    }

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', () => {
      if (playing) start();
    });

    show(0);
    if (playing) start();
  }

  /* ---------------------------------------------------------------------- boot */

  function boot() {
    document.querySelectorAll('[data-tf-hero]').forEach(initHero);
    syncWishlist();
    refreshProgress();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor re-renders sections without a page load
  document.addEventListener('shopify:section:load', boot);
})();
